import React from "react";
import {
  PieChart,
  ArrowUpDown,
  Search,
  AlertTriangle,
  Activity,
  BookOpen,
  Sparkles,
} from "lucide-react";
import { ASSETS } from "@/lib/assets";

const FEATURES = [
  {
    icon: PieChart,
    name: "Portfolio Check",
    desc: "Analyze your holdings and unrealized P&L with one tap.",
    accent: "bg-[#EDE9FE] text-[#5B21B6]",
  },
  {
    icon: ArrowUpDown,
    name: "Hold or Sell Review",
    desc: "Compare trend, risk, and position size before you act.",
    accent: "bg-[#FCE7F3] text-[#9D174D]",
  },
  {
    icon: Search,
    name: "Stock Research",
    desc: "Summarize price action, fundamentals, and news signals.",
    accent: "bg-[#DBEAFE] text-[#1E40AF]",
  },
  {
    icon: AlertTriangle,
    name: "Risk Alert",
    desc: "Flag volatility, concentration, and downside pressure.",
    accent: "bg-[#FFEDD5] text-[#9A3412]",
  },
  {
    icon: Activity,
    name: "Quant Signals",
    desc: "Track momentum, reversal, volume and factor scores.",
    accent: "bg-[#D1FAE5] text-[#065F46]",
  },
  {
    icon: BookOpen,
    name: "Learn with Fifi",
    desc: "Understand investing logic in simple, clear language.",
    accent: "bg-[#FEF3C7] text-[#92400E]",
  },
];

const FifiQuantAgent = ({ onNotifyClick }) => {
  return (
    <section
      id="fifi-quant"
      data-testid="fifi-quant-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 brand-gradient-soft opacity-50" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EDE9FE] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#5B21B6]">
            <Sparkles size={14} /> Fifi Quant Agent
          </span>
          <h2 className="font-display mt-5 text-4xl sm:text-5xl font-black tracking-tighter text-slate-900">
            Six AI agents.{" "}
            <span className="brand-gradient-text">One quant brain.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Fifi is training to read charts, risk, and portfolios for you —
            powered by a fine-tuned financial LLM with chain-of-thought
            reasoning.
          </p>
          {/* "Hi I'm Fifi" promo right below the heading */}
          <img
            src={ASSETS.fifiQuant}
            alt="Hi, I'm Fifi — Your Quant Cat Advisor"
            width={1254}
            height={1254}
            data-testid="fifi-quant-promo"
            className="mx-auto mt-8 w-full h-auto max-w-md md:max-w-lg object-contain"
          />
        </div>

        {/* Spotlight tile — centered, phone + text close together */}
        <div
          data-testid="quant-feature-spotlight"
          className="relative rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6 lg:mb-8 max-w-5xl mx-auto"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#EEF2FF] via-white to-[#FCE7F3] opacity-70" />
          <div className="relative grid md:grid-cols-2 gap-4 md:gap-8 items-center p-8 md:p-10 justify-items-center">
            <div className="max-w-sm text-center md:text-left order-2 md:order-1">
              <span className="inline-flex items-center gap-1 rounded-full bg-white/90 border border-[#EDE9FE] px-3 py-0.5 text-[10px] uppercase tracking-[0.18em] font-bold text-[#5B21B6]">
                Coming Soon · Live in App
              </span>
              <h3 className="font-display mt-4 text-3xl md:text-4xl font-black text-slate-900 leading-tight">
                Fifi is training
                <br />
                to read charts for you.
              </h3>
              <p className="mt-4 text-slate-600">
                Multimodal data fusion, hierarchical memory, and real-time
                market analysis. 40% more accurate. 50% sharper insights.
              </p>
              <button
                type="button"
                data-testid="quant-notify-me"
                onClick={onNotifyClick}
                className="mt-6 inline-flex items-center gap-2 rounded-full brand-gradient text-white px-6 py-3 font-bold text-sm shadow-[0_8px_24px_-8px_rgba(168,85,247,0.6)] hover:-translate-y-0.5 transition-transform border-0 cursor-pointer"
              >
                <Sparkles size={14} /> Notify Me
              </button>
            </div>
            {/* Phone mockup with actual app screen */}
            <div className="flex justify-center order-1 md:order-2">
              <div className="relative w-[240px] sm:w-[270px]">
                <div className="absolute inset-0 brand-gradient-soft rounded-[3rem] blur-2xl opacity-80 scale-110" />
                <div className="relative rounded-[2.5rem] bg-slate-900 p-3 shadow-2xl">
                  <div className="rounded-[2rem] overflow-hidden bg-white">
                    <img
                      src="/assets/app/quant-screen.png"
                      alt="Fifi Quant Agent — Coming Soon app screen"
                      width={852}
                      height={1846}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
                {/* Floating chip */}
                <div className="absolute -top-3 -left-4 rotate-[-6deg] bg-white rounded-2xl px-4 py-2 shadow-xl border border-slate-100">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">
                    Fifi · Quant Agent
                  </div>
                  <div className="inline-flex items-center gap-1 mt-0.5">
                    <span className="text-xs font-display font-black text-slate-900">1,493 XP</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.name}
                data-testid={`quant-feature-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative rounded-3xl bg-white border border-slate-100 p-6 hover:-translate-y-1 transition-transform duration-300 shadow-[0_4px_20px_rgb(0,0,0,0.03)]"
              >
                <div
                  className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${f.accent} mb-4`}
                >
                  <Icon size={22} />
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                  0{i + 1}
                </div>
                <h4 className="font-display mt-1 text-lg font-bold text-slate-900">
                  {f.name}
                </h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FifiQuantAgent;
